import type { SelectedGraphs } from '../components/SharedComponents/ExportDialog';

export type LegendItemType = 'default' | 'line' | 'dashed-line' | 'striped';

export type LegendItem = {
  color: string;
  label: string;
  type: LegendItemType;
  stripeColors?: { light: string; dark: string };
};

export type GraphImage = {
  name: string;
  imageData: string;
  legend: Array<LegendItem>;
  description: string;
};

type GraphMapping = {
  id: string;
  name: string;
  selector: string;
};

const graphMappings: Record<string, Array<GraphMapping>> = {
  Overview: [
    {
      id: 'prospects',
      name: 'Total listing prospects',
      selector: '[data-graph-id="prospects"]',
    },
    {
      id: 'page-views',
      name: 'Page Views',
      selector: '[data-graph-id="page-views"]',
    },
    {
      id: 'impressions',
      name: 'Impressions',
      selector: '[data-graph-id="impressions"]',
    },
    {
      id: 'active-properties',
      name: 'Active Properties',
      selector: '[data-graph-id="active-properties"]',
    },
  ],
  'Ad Spend': [
    {
      id: 'advertising-spend',
      name: 'Advertising Spend',
      selector: '[data-graph-id="advertising-spend"]',
    },
  ],
  'Market Data': [
    {
      id: 'rent-price',
      name: 'Multifamily Rent',
      selector: '[data-graph-id="rent-price"]',
    },
    {
      id: 'single-family-rent',
      name: 'Single Family Rent',
      selector: '[data-graph-id="single-family-rent"]',
    },
    {
      id: 'vacancy-rates',
      name: 'Vacancy Rates',
      selector: '[data-graph-id="vacancy-rates"]',
    },
    {
      id: 'vacant-units',
      name: 'Vacant Units',
      selector: '[data-graph-id="vacant-units"]',
    },
  ],
  'Renter Data': [
    {
      id: 'summary-cards',
      name: 'Summary Metrics',
      selector: '[data-graph-id="summary-cards"]',
    },
    {
      id: 'pet-ownership',
      name: 'Pet Ownership',
      selector: '[data-graph-id="pet-ownership"]',
    },
    {
      id: 'bedrooms-searched',
      name: 'Bedrooms Searched',
      selector: '[data-graph-id="bedrooms-searched"]',
    },
    {
      id: 'bathrooms-searched',
      name: 'Bathrooms Searched',
      selector: '[data-graph-id="bathrooms-searched"]',
    },
  ],
};

const extractLegendFromContainer = (container: HTMLElement): Array<LegendItem> => {
  const legendWrapper = container.querySelector('.recharts-legend-wrapper');
  if (!legendWrapper) return [];

  const flexContainer = legendWrapper.firstElementChild;
  if (!flexContainer) return [];

  const items: Array<LegendItem> = [];

  for (const child of Array.from(flexContainer.children)) {
    if (!(child instanceof HTMLElement)) continue;

    let color = '#000000';
    let itemType: LegendItemType = 'default';
    let stripeColors: { light: string; dark: string } | undefined;

    const svg = child.querySelector('svg');
    if (svg) {
      const patternRects = svg.querySelectorAll('pattern rect');
      if (patternRects.length >= 2) {
        itemType = 'striped';
        const lightRect = patternRects[0] as SVGRectElement;
        const darkRect = patternRects[1] as SVGRectElement;
        const light = lightRect.getAttribute('fill') ?? '#B88DFC';
        const dark = darkRect.getAttribute('fill') ?? '#891EFA';
        stripeColors = { light, dark };
        color = dark;
      }
    }

    if (itemType === 'default') {
      const spans = child.querySelectorAll('span');
      for (const span of Array.from(spans)) {
        if (!(span instanceof HTMLElement)) continue;
        const computed = window.getComputedStyle(span);
        const borderStyle = computed.borderBottomStyle;
        const borderColor = span.style.borderColor || computed.borderBottomColor;

        if (borderStyle === 'dashed' && borderColor) {
          itemType = 'dashed-line';
          color = borderColor;
          break;
        }
        if (borderStyle === 'solid' && borderColor && computed.borderBottomWidth !== '0px') {
          itemType = 'line';
          color = borderColor;
          break;
        }

        const bgColor = span.style.backgroundColor;
        if (bgColor) {
          color = bgColor;
          break;
        }
      }
    }

    const label = child.textContent?.trim() ?? '';
    if (label) {
      items.push({ color, label, type: itemType, stripeColors });
    }
  }

  return items;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getComputedFontFamily = (svg: SVGSVGElement): string => {
  const textEl = svg.querySelector('text');
  if (textEl) {
    const computed = window.getComputedStyle(textEl).fontFamily;
    if (computed) return computed;
  }
  const computed = window.getComputedStyle(svg).fontFamily;
  return computed || 'sans-serif';
};

const svgToDataURL = (svg: SVGSVGElement): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // Clone the SVG to avoid modifying the original
      const clonedSvg = svg.cloneNode(true) as SVGSVGElement;

      // Get dimensions
      const bbox = svg.getBoundingClientRect();
      const width = bbox.width;
      const height = bbox.height;

      // Set explicit dimensions on cloned SVG
      clonedSvg.setAttribute('width', String(width));
      clonedSvg.setAttribute('height', String(height));

      // Embed the computed font-family so it survives blob URL serialization
      const fontFamily = getComputedFontFamily(svg);
      const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleEl.textContent = `text, tspan { font-family: ${fontFamily}; }`;
      clonedSvg.insertBefore(styleEl, clonedSvg.firstChild);

      // Serialize SVG to string
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clonedSvg);

      // Create a blob from SVG string
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      // Create image and canvas
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Set canvas size with scale for higher quality
      const scale = 2;
      canvas.width = width * scale;
      canvas.height = height * scale;
      ctx.scale(scale, scale);

      img.onload = () => {
        // Draw white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        // Draw image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to PNG
        const dataURL = canvas.toDataURL('image/png', 0.95);

        URL.revokeObjectURL(url);
        resolve(dataURL);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load SVG image'));
      };

      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
};

export const captureGraphs = async (
  tabName: 'Overview' | 'Ad Spend' | 'Market Data' | 'Renter Data',
  selectedGraphs: SelectedGraphs,
): Promise<Array<GraphImage>> => {
  const mappings = graphMappings[tabName];
  if (!mappings) {
    return [];
  }

  const graphImages: Array<GraphImage> = [];

  for (const mapping of mappings) {
    if (!selectedGraphs[mapping.id]) {
      continue;
    }

    const element = document.querySelector(mapping.selector);
    if (!element || !(element instanceof HTMLElement)) {
      continue;
    }

    // Small delay to keep UI responsive
    await delay(50);

    // Find the Recharts SVG element (it has class "recharts-surface")
    // This avoids selecting icon SVGs or other SVG elements
    const svgElement = element.querySelector('svg.recharts-surface');
    if (!svgElement || !(svgElement instanceof SVGSVGElement)) {
      // Skip sections that don't have Recharts charts (they will be exported as text in PDF)
      continue;
    }

    // Convert SVG directly to image (much faster than html2canvas)
    const imageData = await svgToDataURL(svgElement);
    const legend = extractLegendFromContainer(element);
    const heading = element.querySelector('h5');
    const descriptionEl = heading?.parentElement?.querySelector('p');
    const description = descriptionEl?.textContent?.trim() ?? '';

    graphImages.push({
      name: mapping.name,
      imageData,
      legend,
      description,
    });

    // Small delay between captures
    await delay(50);
  }

  return graphImages;
};
